import React, { FC, useEffect } from 'react';
import Lottie from 'react-lottie';
import { useRouter } from 'next/router';
import { getAccessToken } from '@repository/AccessToken';
import bangMark from '@graphic/bangMark.json';
import ResultVO from '@domain/vo/phs/test/ResultVO';
import { PHSAnalysis } from '@domain/constant/phsAnalysisType';
import { ContentRouteUtil, RouteUrl, V2Route } from '@utils/routeUtil';

import classnames from 'classnames/bind';
import styles from './resultGate.module.scss';
import { usePHSApplyResultList } from '@repository/phs/apply/usePhsApply';
const cx = classnames.bind(styles);

interface IResultCardProps {
  vo: ResultVO;
  type: 'PST' | 'PACC';
}

const ResultCard: FC<IResultCardProps> = ({ vo, type }) => {
  const { applySn, analysisText, createdDateTime, analysisType } = vo;
  const routeUrl =
    type === 'PST' ? V2Route.getPhsPstApplyResultPage(applySn) : V2Route.getPhsPaccApplyResultPage(applySn);
  const title = type === 'PST' ? '개발 구현 능력 검사' : '개발자 역검';

  const onClickResult = () => {
    if (PHSAnalysis.COMPLETED === analysisType) {
      window.open(routeUrl);
    }
  };

  return (
    <button
      className={cx('btn')}
      type="button"
      onClick={onClickResult}
      disabled={PHSAnalysis.IN_PROGRESS === analysisType}
    >
      <div className={cx('textWrap')}>
        <span
          className={cx(
            'badge',
            { type1: PHSAnalysis.IN_PROGRESS === analysisType },
            { type2: PHSAnalysis.FAILED === analysisType },
          )}
        >
          {analysisText}
        </span>
        {title}
        <span className={cx('date')}>{createdDateTime}</span>
      </div>
    </button>
  );
};

const options = {
  loop: true,
  autoplay: true,
  animationData: bangMark,
};

const PhsResult: FC = () => {
  const { back, push } = useRouter();
  const { data, isFetched } = usePHSApplyResultList();

  useEffect(() => {
    if (!getAccessToken()) {
      ContentRouteUtil.goLoginPage();
    }
  }, []);

  useEffect(() => {
    if (isFetched) {
      if (!data?.paccResults.length) {
        //응시 이력이 없을 경우 개종검 게이트 페이지로 이동
        push(RouteUrl.PHS_TEST_GATE);
      }
    }
  }, [isFetched]);

  if (!data) return null;

  const paccResultListEl = data.paccResults.map((item) => {
    return <ResultCard key={item.applySn} vo={item} type={'PACC'} />;
  });

  const pstResultListEl = data.pstResults.map((item) => {
    return <ResultCard key={item.applySn} vo={item} type={'PST'} />;
  });

  return (
    <div className={cx('resultGateArea')}>
      <div className={cx('visualArea')}>
        <div className={cx('visualWrap')}>
          <Lottie options={options} />
        </div>
      </div>

      <div className={cx('infoArea')}>
        <a className={cx('btnPrev')} role="button" onClick={back}>
          이전
        </a>

        <div className={cx('infoInner')}>
          <strong className={cx('title')}>
            매칭과 입사 지원에는
            <br />
            개인별 최적의 결과가 자동 제출됩니다.
          </strong>

          <div className={cx('btnArea')}>
            <div className={cx('titleArea')}>
              <span className={cx('resultTitle')}>개발자 역검 결과표</span>
              <div className={cx('notice')}>분석은 최대 1일 가량 소요될 수 있습니다.</div>
            </div>
            {paccResultListEl}
          </div>

          <div className={cx('btnArea')}>
            <div className={cx('titleArea')}>
              <span className={cx('resultTitle')}>개발 구현 능력 검사 결과표</span>
              <div className={cx('notice')}>분석은 최대 10분 가량 소요될 수 있습니다.</div>
            </div>
            {pstResultListEl}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhsResult;
